import {
  saveToken,
  useResetPasswordMutation,
  useSetPasswordMutation,
} from "@/src/services/api/auth.api";
import { zodResolver } from "@hookform/resolvers/zod";
import { router, useLocalSearchParams } from "expo-router";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Toast from "react-native-toast-message";
import { z } from "zod";

const resetPasswordSchema = z
  .object({
    password: z
      .string({
        required_error: "ERROR_NO_PASSWORD",
      })
      .min(1, { message: "ERROR_NO_PASSWORD" })
      .min(8, { message: "ERROR_PASSWORD_LENGTH" }),
    confirmPassword: z
      .string({
        required_error: "ERROR_PASSWORD_CONFIRM",
      })
      .min(1, { message: "ERROR_PASSWORD_CONFIRM" }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "ERROR_PASSWORD_NOT_MATCH",
    path: ["confirmPassword"],
  });

export type ResetPasswordForm = Readonly<z.infer<typeof resetPasswordSchema>>;

type PasswordScreenType = "reset-password" | "set-password";

export const useResetPassword = (
  type: PasswordScreenType = "reset-password",
) => {
  const [formError, setFormError] = useState<string | undefined>();
  const [mounted, setMounted] = useState(false);
  const { t } = useTranslation();
  const params = useLocalSearchParams();
  const { code } = params;

  useEffect(() => {
    setMounted(true);
  }, []);

  const form = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = form;

  const password = watch("password");

  const resetPasswordMutation = useResetPasswordMutation({
    onSuccess: async (data) => {
      await saveToken(data.access_token);
      Toast.show({
        type: "success",
        text1: t("SUCCESS_PASSWORD_RESET"),
      });
      router.replace("/login");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || t("ERROR_INVALID_PW_EMAIL");
      setFormError(errorMessage);
    },
  });

  const setPasswordMutation = useSetPasswordMutation({
    onSuccess: async (data) => {
      await saveToken(data.access_token);
      Toast.show({
        type: "success",
        text1: t("SUCCESS_PASSWORD_SET"),
      });
      router.replace("/login");
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message || t("ERROR_INVALID_PW_EMAIL");
      setFormError(errorMessage);
    },
  });

  const onSubmit = (data: ResetPasswordForm) => {
    setFormError(undefined);

    if (type === "reset-password") {
      resetPasswordMutation.mutate({
        password: data.password,
        code: code as string,
      });
    } else {
      setPasswordMutation.mutate({
        password: data.password,
        code: code as string,
      });
    }
  };

  const isSubmitting =
    type === "reset-password"
      ? resetPasswordMutation.isPending
      : setPasswordMutation.isPending;

  return {
    control,
    handleSubmit,
    errors,
    password,

    formError,
    mounted,
    isSubmitting,

    onSubmit,

    t,
  };
};
